import { MOCK_GROUP_PERSONS } from './group-chat'
import type { GroupChatMember } from './group-chat'
import { formatMessagePreview } from './message-format'
import type { ChatMessage, Conversation, ConversationMember } from '../types'

const MEMBER_AVATAR_COLORS = [
  'var(--z-color-primary)',
  'var(--z-color-success)',
  'var(--z-color-warning)',
  'var(--z-color-danger)',
  'var(--z-color-info)',
] as const

export function canReply(conversation: Conversation | null | undefined) {
  return conversation?.kind === 'direct' || conversation?.kind === 'group'
}

export function memberInitial(name: string) {
  const trimmed = name.trim()
  return trimmed ? [...trimmed][0]! : '?'
}

export function memberAvatarColor(id: string) {
  let hash = 0
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0
  }
  return MEMBER_AVATAR_COLORS[Math.abs(hash) % MEMBER_AVATAR_COLORS.length]
}

export function nextMessageId(prefix = 'msg') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
}

function memberSubtitle(member: GroupChatMember) {
  if (member.kind === 'bot') return '智能助手'
  const rawId = member.id.replace(/^user:/, '')
  const person = MOCK_GROUP_PERSONS.find((item) => item.id === rawId)
  if (!person) return undefined
  return `${person.deptName} · ${person.title}`
}

export function toConversationMember(member: GroupChatMember): ConversationMember {
  const rawId = member.id.replace(/^user:/, '')
  const person = MOCK_GROUP_PERSONS.find((item) => item.id === rawId)
  return {
    id: member.id,
    name: member.name,
    kind: member.kind,
    username: member.username ?? person?.username,
    email: member.email ?? person?.email,
  }
}

export function formatMemberMention(name: string) {
  return `@${name.trim()} `
}

export function appendMemberMention(draft: string, name: string) {
  const token = formatMemberMention(name)
  if (!draft) return token
  return /[\s\u00a0]$/.test(draft) ? `${draft}${token}` : `${draft} ${token}`
}

export function canDirectChat(member: ConversationMember | GroupChatMember) {
  return member.kind == null || member.kind === 'user'
}

export function buildConversationFromMembers(members: GroupChatMember[]): {
  conversation: Conversation
  messages: ChatMessage[]
} {
  const people = members.map(toConversationMember)
  const names = people.map((item) => item.name)
  const kind = people.length === 1 ? 'direct' : 'group'
  const title =
    kind === 'direct'
      ? names[0]!
      : names.length <= 3
        ? names.join('、')
        : `${names[0]}等${names.length}人`
  const id = `${kind === 'direct' ? 'd' : 'g'}-${Date.now()}`
  const createTime = new Date().toISOString()
  const systemText =
    kind === 'direct'
      ? `已发起与${title}的会话`
      : `你邀请了${names.join('、')}加入群聊`

  const systemMessage: ChatMessage = {
    id: nextMessageId('sys'),
    conversationId: id,
    from: 'system',
    kind: 'text',
    content: systemText,
    createTime,
  }

  return {
    conversation: {
      id,
      kind,
      title,
      subtitle: kind === 'direct' ? memberSubtitle(members[0]!) : `${people.length} 人`,
      online: kind === 'direct',
      lastPreview: formatMessagePreview(systemMessage),
      lastTime: createTime,
      unread: 0,
      sender: '我',
      members: people,
    },
    messages: [systemMessage],
  }
}
